import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';

/**
 * Error Monitoring Service with Sentry Integration
 * Provides comprehensive error tracking, performance monitoring, and user context
 */

class ErrorMonitoringService {
  constructor() {
    this.isInitialized = false;
    this.isEnabled = import.meta.env?.VITE_ENABLE_SENTRY === 'true';
    this.errorBuffer = [];
    this.maxBufferSize = 100;
    this.performanceMetrics = new Map();
  }

  /**
   * Initialize Sentry SDK
   */
  initialize() {
    if (this.isInitialized || !this.isEnabled) {
      console.log('Sentry error monitoring:', this.isEnabled ? 'Already initialized' : 'Disabled');
      return;
    }

    const dsn = import.meta.env?.VITE_SENTRY_DSN;
    if (!dsn || dsn === 'your-sentry-dsn-here') {
      console.warn('Sentry DSN not configured. Error monitoring disabled.');
      return;
    }

    try {
      Sentry?.init({
        dsn,
        environment: import.meta.env?.VITE_SENTRY_ENVIRONMENT || 'development',
        integrations: [
          Sentry?.browserTracingIntegration(),
          Sentry?.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        // Performance Monitoring
        tracesSampleRate: parseFloat(import.meta.env?.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'),
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        // Release tracking
        release: `recrutees@${import.meta.env?.VITE_APP_VERSION || '1.0.0'}`,
        // Error filtering
        beforeSend(event, hint) {
          // Filter out non-critical errors
          if (event?.exception?.values?.[0]?.value?.includes('ResizeObserver')) {
            return null;
          }
          return event;
        },
        // Breadcrumbs
        maxBreadcrumbs: 50,
        // Debug mode in development
        debug: import.meta.env?.MODE === 'development',
      });

      this.isInitialized = true;
      console.log('✅ Sentry error monitoring initialized');
    } catch (error) {
      console.error('Failed to initialize Sentry:', error);
    }
  }

  /**
   * Set user context for error tracking
   */
  setUserContext(user) {
    if (!this.isInitialized) return;

    try {
      Sentry?.setUser({
        id: user?.id,
        email: user?.email,
        username: user?.user_metadata?.full_name || user?.email?.split('@')?.[0],
        role: user?.user_metadata?.role || 'unknown',
      });
    } catch (error) {
      console.error('Failed to set Sentry user context:', error);
    }
  }

  /**
   * Clear user context (on logout)
   */
  clearUserContext() {
    if (!this.isInitialized) return;

    try {
      Sentry?.setUser(null);
    } catch (error) {
      console.error('Failed to clear Sentry user context:', error);
    }
  }

  /**
   * Set custom context tags
   */
  setContext(key, value) {
    if (!this.isInitialized) return;

    try {
      Sentry?.setContext(key, value);
    } catch (error) {
      console.error('Failed to set Sentry context:', error);
    }
  }

  /**
   * Add breadcrumb for tracking user actions
   */
  addBreadcrumb(message, category = 'user-action', level = 'info', data = {}) {
    if (!this.isInitialized) return;

    try {
      Sentry?.addBreadcrumb({
        message,
        category,
        level,
        data,
        timestamp: Date.now() / 1000,
      });
    } catch (error) {
      console.error('Failed to add Sentry breadcrumb:', error);
    }
  }

  /**
   * Capture exception with context
   */
  captureException(error, context = {}) {
    if (!this.isInitialized) {
      // Buffer errors if Sentry not initialized
      this.bufferError({ error, context, timestamp: Date.now() });
      console.error('Error (Sentry disabled):', error, context);
      return null;
    }

    try {
      const eventId = Sentry?.captureException(error, {
        tags: context?.tags || {},
        extra: context?.extra || {},
        level: context?.level || 'error',
        contexts: context?.contexts || {},
      });

      // Also log to database for internal tracking
      this.logErrorToDatabase(error, context, eventId);

      return eventId;
    } catch (err) {
      console.error('Failed to capture exception in Sentry:', err);
      return null;
    }
  }

  /**
   * Capture message (non-error events)
   */
  captureMessage(message, level = 'info', context = {}) {
    if (!this.isInitialized) {
      console.log(`Message (Sentry disabled) [${level}]:`, message, context);
      return null;
    }

    try {
      return Sentry?.captureMessage(message, {
        level,
        tags: context?.tags || {},
        extra: context?.extra || {},
      });
    } catch (error) {
      console.error('Failed to capture message in Sentry:', error);
      return null;
    }
  }

  /**
   * Start performance transaction
   */
  startTransaction(name, op = 'custom') {
    if (!this.isInitialized) {
      return {
        finish: () => {},
        setStatus: () => {},
        setTag: () => {},
        setData: () => {},
      };
    }

    try {
      const transaction = Sentry?.startSpan({
        name,
        op,
      }, (span) => {
        return {
          finish: () => span?.end(),
          setStatus: (status) => span?.setStatus(status),
          setTag: (key, value) => span?.setAttribute(key, value),
          setData: (key, value) => span?.setAttribute(key, value),
        };
      });

      return transaction || {
        finish: () => {},
        setStatus: () => {},
        setTag: () => {},
        setData: () => {},
      };
    } catch (error) {
      console.error('Error starting transaction:', error);
      return {
        finish: () => {},
        setStatus: () => {},
        setTag: () => {},
        setData: () => {},
      };
    }
  }

  /**
   * End performance transaction
   */
  endTransaction(name) {
    if (!this.isInitialized) return;

    try {
      const metric = this.performanceMetrics?.get(name);
      if (metric?.transaction) {
        metric?.transaction?.finish();
        this.performanceMetrics?.delete(name);
      }
    } catch (error) {
      console.error('Failed to end Sentry transaction:', error);
    }
  }

  /**
   * Track API call performance
   */
  trackApiCall(endpoint, method, duration, status) {
    this.addBreadcrumb(
      `API ${method} ${endpoint}`,
      'http',
      status >= 400 ? 'error' : 'info',
      { endpoint, method, duration, status }
    );
  }

  /**
   * Track page navigation
   */
  trackNavigation(from, to) {
    this.addBreadcrumb(
      `Navigation: ${from} → ${to}`,
      'navigation',
      'info',
      { from, to }
    );
  }

  /**
   * Buffer errors when Sentry is not initialized
   */
  bufferError(errorData) {
    if (this.errorBuffer?.length >= this.maxBufferSize) {
      this.errorBuffer?.shift(); // Remove oldest
    }
    this.errorBuffer?.push(errorData);
  }

  /**
   * Get buffered errors
   */
  getBufferedErrors() {
    return [...this.errorBuffer];
  }

  /**
   * Clear error buffer
   */
  clearErrorBuffer() {
    this.errorBuffer = [];
  }

  /**
   * Log error to Supabase for internal tracking
   */
  async logErrorToDatabase(error, context, sentryEventId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      await supabase?.from('error_logs')?.insert({
        error_message: error?.message || 'Unknown error',
        error_stack: error?.stack,
        error_name: error?.name,
        context: context,
        sentry_event_id: sentryEventId,
        user_id: user?.id,
        url: window.location?.href,
        user_agent: navigator.userAgent,
        timestamp: new Date()?.toISOString(),
      });
    } catch (err) {
      // Silently fail - don't want error logging to cause more errors
      console.error('Failed to log error to database:', err);
    }
  }

  /**
   * Get error statistics
   */
  async getErrorStats(timeRange = '24h') {
    try {
      const hoursAgo = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 1;
      const startTime = new Date(Date.now() - hoursAgo * 60 * 60 * 1000)?.toISOString();

      const { data, error } = await supabase?.from('error_logs')?.select('*')?.gte('timestamp', startTime)?.order('timestamp', { ascending: false });

      if (error) throw error;

      return {
        total: data?.length || 0,
        byType: this.groupBy(data, 'error_name'),
        byUser: this.groupBy(data, 'user_id'),
        recent: data?.slice(0, 10) || [],
      };
    } catch (error) {
      console.error('Failed to get error stats:', error);
      return { total: 0, byType: {}, byUser: {}, recent: [] };
    }
  }

  /**
   * Helper: Group array by key
   */
  groupBy(array, key) {
    return array?.reduce((result, item) => {
      const groupKey = item?.[key] || 'unknown';
      result[groupKey] = (result?.[groupKey] || 0) + 1;
      return result;
    }, {}) || {};
  }

  /**
   * Show user feedback dialog
   */
  showReportDialog(eventId) {
    if (!this.isInitialized || !eventId) return;

    try {
      Sentry?.showReportDialog({
        eventId,
        title: 'It looks like we\'re having issues.',
        subtitle: 'Our team has been notified.',
        subtitle2: 'If you\'d like to help, tell us what happened below.',
        labelName: 'Name',
        labelEmail: 'Email',
        labelComments: 'What happened?',
        labelClose: 'Close',
        labelSubmit: 'Submit',
        errorGeneric: 'An error occurred while submitting your report. Please try again.',
        errorFormEntry: 'Some fields were invalid. Please correct the errors and try again.',
        successMessage: 'Your feedback has been sent. Thank you!',
      });
    } catch (error) {
      console.error('Failed to show Sentry report dialog:', error);
    }
  }
}

// Export singleton instance
export const errorMonitoringService = new ErrorMonitoringService();
export default errorMonitoringService;
